<template>
  <CustomModal v-if="!!transaction" @close="$emit('close')" :position="position">
    <template v-slot:body>
      <div class="bg-block rounded -m-8 -m-12 grid grid-cols-12 transaction-edit-modal">
        <div class="col-span-3
                    p-8 lg:p-12 lg:pb-6 lg:pl-14
                    bg-transaction-gradient">
          <div class="text-xl font-bold text-text leading-tight ">
            {{ isTransactionExpense ? $t('editTitleExpense') : $t('editTitleIncome') }}
          </div>
        </div>
        <div class="col-span-9 pt-8 pl-8 pr-8 lg:pt-11 lg:pl-12 lg:pr-17">
          <div class="text-md text-text-ternary leading-relaxed pt-1">
            {{ isTransactionExpense ? $t('editTipExpense') : $t('editTipIncome') }}
          </div>
        </div>
        <div class="col-span-12 grid grid-cols-12">
          <div class="col-span-3 bg-transaction-gradient"></div>
          <div class="col-span-9 pl-8 pr-12 pt-4 lg:px-12">
            <div class="col-span-2 p-5 bg-yellow-extraLight border rounded border-yellow flex items-center">
              <svg width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg"
                   class="flex-none ml-2 mr-6">
                <g>
                  <path
                      d="M11.4453 0C5.37984 0 0.445312 4.93453 0.445312 11C0.445312 17.0655 5.37984 22 11.4453 22C17.5108 22 22.4453 17.0655 22.4453 11C22.4453 4.93453 17.5108 0 11.4453 0ZM11.4453 20.625C6.13816 20.625 1.82031 16.3072 1.82031 11C1.82031 5.69284 6.13781 1.375 11.4453 1.375C16.7525 1.375 21.0703 5.69284 21.0703 11C21.0703 16.3072 16.7525 20.625 11.4453 20.625Z"
                      fill="#C3AD84"/>
                  <path
                      d="M12.5101 5.47955C12.2609 5.22621 11.9563 5.09937 11.5954 5.09937C11.2392 5.09937 10.9357 5.22621 10.6841 5.47955C10.4328 5.7329 10.3066 6.03712 10.3066 6.39187C10.3066 6.7418 10.4335 7.04327 10.6875 7.29662C10.9415 7.54996 11.2447 7.6768 11.5954 7.6768C11.9508 7.6768 12.2547 7.55168 12.5063 7.3004C12.7579 7.0498 12.8837 6.74696 12.8837 6.39187C12.8837 6.03677 12.7593 5.7329 12.5101 5.47955Z"
                      fill="#C3AD84"/>
                  <path
                      d="M12.7466 14.189V8.50781H9.26953V10.208H10.3905V14.189H9.26953V15.8892H13.8445V14.189H12.7466Z"
                      fill="#C3AD84"/>
                </g>
              </svg>
              <p class="text-text mb-0 text-2sm leading-relaxed"
                 v-html="isTransactionExpense ?  $t('infoTransformIncome') : $t('infoTransformExpense') "></p>
            </div>
          </div>
        </div>
        <div class="col-span-12 grid grid-cols-12">
          <div class="col-span-3 p-8 pt-16 lg:p-12 lg:pl-14 bg-transaction-gradient
                space-y-12">
            <ValueInput v-model.trim="$v.form.sum_from.$model"
                        :label="$t('sum_from')"
                        :currency_id="!!form.account_from ? form.account_from.currency.id : null"
                        :class="{'--error':$v.form.sum_from.$error}"
                        :disabled="isTransactionExpense">
              <template v-slot:errors="">
                <div class="error" v-if="$v.form.sum_from.$invalid">{{ $t('fieldRequired') }}</div>
              </template>
            </ValueInput>
            <ValueInput v-if="!!form.account_from && !!form.account_from.commission"
                        v-model.trim="$v.form.fee.$model"
                        :label="$t('commission')"
                        :currency_id="!!form.account_from ? form.account_from.currency.id : null"
                        :class="{'--error':$v.form.fee.$error}"
                        :disabled="isTransactionExpense">
              <template v-slot:errors="">
                <div class="error" v-if="$v.form.fee.$invalid">{{ $t('fieldRequired') }}</div>
              </template>
            </ValueInput>
            <div class="h-16" v-else-if="displayNewTransactionSelect && isTransactionIncome"></div>
            <ValueInput v-model.trim="$v.form.sum_to.$model"
                        :label="$t('sum_to')"
                        :currency_id="!!form.account_to ? form.account_to.currency.id : null"
                        :class="{'--error':$v.form.sum_to.$error}"
                        :disabled="isTransactionIncome">
              <template v-slot:errors="">
                <div class="error" v-if="$v.form.sum_to.$invalid">{{ $t('fieldRequired') }}</div>
              </template>
            </ValueInput>
          </div>
          <div class="col-span-9 p-8 pt-16 lg:p-12 lg:pr-17">
            <div class="form" :class="{'selecting-expense':isTransactionIncome}">
              <div class="account-from-select grid grid-cols-2 col-gap-12 items-center">
                <AccountsSelect :value="form.account_from"
                                :label="$t('account_from')"
                                :accounts="isTransactionExpense ? accounts.data : availableAccounts"
                                :account_groups="isTransactionExpense ? accounts_by_groups : availableAccountGroups"
                                :grouped="accounts_by_groups.length > 1"
                                :disabled="isTransactionExpense"
                                @input="handleSelect"/>
                <div>
                  <div v-if="isTransactionExpense" class="text-sm text-text-ternary">
                    {{ $moment.unix(transaction.date).format('DD.MM.YYYY HH:mm') }}
                  </div>
                  <BaseSelect v-else-if="displayNewTransactionSelect" v-model="form.expenseCategory"
                              :selectLabel="$t('categoryExpense')"
                              :options="expenses_by_groups"
                              :allow-empty="true"
                              :group-select="false"
                              :searchable="true"
                              :show-labels="false"
                              track-by="id"
                              label="title"
                              group-label="title"
                              group-values="childs"/>
                </div>
              </div>
              <div class="account-to-select grid grid-cols-2 col-gap-12 items-center">
                <AccountsSelect :value="form.account_to"
                                :label="$t('account_to')"
                                :accounts="isTransactionIncome ? accounts.data : availableAccounts"
                                :account_groups="isTransactionIncome ? accounts_by_groups : availableAccountGroups"
                                :grouped="accounts_by_groups.length > 1"
                                :disabled="isTransactionIncome"
                                @input="handleSelect"/>
                <div>
                  <div v-if="isTransactionIncome" class="text-sm text-text-ternary">
                    {{ $moment.unix(transaction.date).format('DD.MM.YYYY HH:mm') }}
                  </div>
                </div>
              </div>
              <div v-if="displayNewTransactionSelect"
                   class="transaction-select grid grid-cols-2 col-gap-12 items-center">
                <BaseSelect v-model="newTransaction"
                            :options="availableTransactions"
                            :select-label="'Операция'"
                            label="title"
                            track-by="id"
                            :allow-empty="false"
                            :searchable="true"
                            :show-labels="false">
                  <template slot="singleLabel" slot-scope="props">
                    {{ formattedTransactionOption(props.option) }}
                  </template>
                  <template v-slot:option="props">
                    <div class="w-full whitespace-normal">
                      {{ formattedTransactionOption(props.option) }}
                    </div>
                  </template>
                </BaseSelect>
              </div>
              <div class="actions flex items-center">
                <BaseButton @click="createTransfer"
                            class="mr-8"
                            style="width: 255px;"
                            :loading="submitted"
                            theme="gray">
                  {{ isTransactionExpense ? $t('applyToExpense') : $t('applyToIncome') }}
                </BaseButton>
                <BaseButton @click="$emit('close')"
                            style="width: 140px;"
                            :loading="submitted"
                            theme="white">
                  {{ $t('cancel') }}
                </BaseButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </CustomModal>
</template>

<script>
import {mapActions, mapGetters, mapState} from "vuex";
import {decimal, required, requiredIf} from "vuelidate/lib/validators";
import CustomModal from "@/components/UI/modals/CustomModal";
import ValueInput from "@/components/UI/BaseInput/ValueInput";
import AccountsSelect from "@/components/UI/BaseSelect/AccountsSelect";
import BaseButton from "@/components/UI/BaseButton";
import BaseSelect from "@/components/UI/BaseSelect/BaseSelect";

export default {
  name: "TransactionToTransferModal",
  components: {
    BaseSelect,
    BaseButton,
    AccountsSelect,
    ValueInput,
    CustomModal,
  },
  props: {
    transaction: {
      required: true
    },
    position: {
      type: String,
      default: 'relative'
    }
  },
  data() {
    return {
      submitted: false,
      fetchedTransactions: [],
      newTransactionObject: null,
      form: {
        newTransaction: null,
        account_from: null,
        sum_from: null,
        fee: null,
        account_to: null,
        sum_to: null,
        expenseCategory: null,
      }
    }
  },
  validations: {
    form: {
      sum_from: {
        required,
        decimal,
      },
      sum_to: {
        required: required,
        decimal,
      },
      fee: {
        required: requiredIf(function () {
          return !!this.form.account_from && this.form.account_from.commission
        }),
        decimal,
      }
    }
  },
  computed: {
    ...mapState('space/accounts', ['accounts']),
    ...mapGetters('space/accounts', ['accounts_by_groups']),
    ...mapGetters('space/expenses', ['expenses_by_groups']),
    ...mapGetters('cabinet/currencies', ['displayCurrencyValue']),
    isTransactionIncome() {
      return !!this.transaction && this.transaction.type === 1;
    },
    isTransactionExpense() {
      return !!this.transaction && this.transaction.type === 2;
    },
    displayNewTransactionSelect() {
      return this.isTransactionIncome && this.form.account_from || this.isTransactionExpense && this.form.account_to;
    },
    newTransactionAccount() {
      return this.isTransactionIncome ? this.form.account_from : this.form.account_to;
    },
    newTransaction: {
      get() {
        return this.form.newTransaction;
      },
      set(value) {
        this.handleTransactionSelect(value)
      }
    },
    availableTransactions() {
      return [this.newTransactionObject, ...this.fetchedTransactions];
    },
    availableAccounts() {
      return this.accounts.data.filter(i => i.id !== this.transaction.account.id);
    },
    availableAccountGroups() {
      return this.accounts_by_groups.map(group => {
        return {
          ...group,
          childs: group.childs.filter(acc => acc.id !== this.transaction.account.id)
        }
      })
    },
  },
  methods: {
    ...mapActions('space/transactions', ['getTransactions', 'transformTransaction']),
    ...mapActions('cabinet/currencies', ['exchangeValues']),
    setFields(transaction) {
      const {account, commission, sum} = transaction;
      if (this.isTransactionIncome) {
        this.form.account_to = account;
        this.form.sum_to = sum;
      } else if (this.isTransactionExpense) {
        this.form.account_from = account;
        this.form.sum_from = !!commission ? sum - commission : sum;
        this.form.fee = !!commission ? commission : 0;
      }
    },
    formattedTransactionOption(transaction) {
      if (!transaction.id) {
        return transaction[`title_${this.$i18n.locale}`]
      } else {
        const date = this.$moment.unix(transaction.date).format('DD.MM.YYYY HH:mm'),
            formattedSum = this.displayCurrencyValue(transaction.sum, transaction.account.currency)
        return `${date} - ${formattedSum}`
      }
    },
    async differentCurrenciesSum(acc_from, acc_to, sum) {
      try {
        let data = {
          currency_from: acc_from.currency.id,
          currency_to: acc_to.currency.id,
          sum,
          date_time: this.$moment.unix(this.transaction.date).format('YYYY-MM-DD HH:mm'),
          timezone: this.$moment.tz.guess()
        }
        const res = await this.exchangeValues(data);
        return +res.body.data.toFixed(this.form.account_to.currency.decimals);
      } catch (e) {
        this.$toasted.error('Unable to convert currencies')
        console.log(e)
      }
    },
    async fetchAccountTransactions() {
      try {
        const query = {
          account_id: this.newTransactionAccount.id,
          type: this.isTransactionIncome ? 2 : 1,
          date_start: this.$moment.unix(this.transaction.date).startOf('day').unix(),
          date_end: this.$moment.unix(this.transaction.date).endOf('day').unix(),
        }
        const res = await this.getTransactions(query);
        this.fetchedTransactions = res.body.data.rows;
      } catch (e) {
        this.$toasted.error('Неудалось загрузить транзакции')
      }
    },
    async updateNewTransactionObject() {
      let sum, commission;
      if (this.isTransactionExpense) {
        sum = this.form.account_from.currency.id === this.form.account_to.currency.id
            ? this.form.sum_from
            : await this.differentCurrenciesSum(this.form.account_from, this.form.account_to, this.form.sum_from);
      } else {
        sum = this.form.account_from.currency.id === this.form.account_to.currency.id
            ? this.form.sum_to
            : await this.differentCurrenciesSum(this.form.account_to, this.form.account_from, this.form.sum_to);
        if (this.form.account_from.commission) {
          if (this.form.account_from.commission_type === 1) {
            commission = this.form.account_from.commission_value * sum / 100;
          } else if (this.form.account_from.commission_type === 2) {
            commission = this.form.account_from.commission_value;
          }
          sum += commission;
        }
      }
      this.newTransactionObject = {
        id: null,
        title_ru: 'Новая транзакция',
        title_en: 'New transaction',
        account: {...this.newTransactionAccount},
        sum,
        commission
      };
    },
    clearTransactionValues() {
      if (this.isTransactionExpense) {
        this.form.sum_to = null;
      } else {
        this.form.sum_from = null;
        this.form.fee = null;
      }
    },
    async handleSelect(account) {
      if (this.isTransactionExpense) {
        this.form.account_to = account;
      } else {
        this.form.account_from = account;
      }
      this.clearTransactionValues();
      await this.updateNewTransactionObject();
      this.newTransaction = this.availableTransactions[0];
      await this.fetchAccountTransactions();
    },
    handleTransactionSelect(new_transaction) {
      this.form.newTransaction = new_transaction;
      const {sum, commission} = new_transaction;
      if (this.isTransactionIncome) {
        this.form.sum_from = !!commission ? sum - commission : sum;
        this.form.fee = !!commission ? commission : null;
      } else if (this.isTransactionExpense) {
        this.form.sum_to = sum;
      }
    },
    async createTransfer() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        try {
          this.submitted = true;
          const data = {
            id: this.transaction.id,
            from_id: this.form.account_from.id,
            to_id: this.form.account_to.id,
            sum_from: !!this.form.fee ? this.form.sum_from + this.form.fee : this.form.sum_from,
            commission: this.form.fee,
            sum_to: this.form.sum_to,
            target_id: this.newTransaction.id,
            category_id: !!this.form.expenseCategory ? this.form.expenseCategory.id : null
          };
          const res = await this.transformTransaction(data)
          if (res.body.success) {
            this.$toasted.success('Перевод успешно создан');
            this.$emit('success');
            this.$emit('close');
          } else {
            this.$toasted.error('Error during creating a transfer');
            console.log(res)
          }
        } catch (err) {
          this.$toasted.error('Error during creating a transfer');
          console.log(err)
        } finally {
          this.submitted = false;
        }
      } else {
        this.$toasted.error('Check the fields');
      }
    }
  },
  mounted() {
    this.setFields(this.transaction)
  },
  i18n: {
    messages: {
      en: {
        editTitleExpense: 'Expense in transfer',
        editTipExpense: 'Convert an expense transaction to an transfer one',
        editTitleIncome: 'Income in transfer',
        editTipIncome: 'Converting a profitable transaction to an transfer one',
        infoTransformExpense: 'Specify the account from which the receipt was received and the expense category. <br> If you have already entered an expense transaction for this account, select it from the list.',
        infoTransformIncome: 'Specify the account to which the receipt was received. <br> If you have already made an income transaction on this account, select it from the list.',
        sum_from: 'Sum',
        sum_to: 'Received amount',
        commission: 'Commission',
        account_from: 'From account',
        account_to: 'To account',
        categoryExpense: 'Expense category',
        changesSaved: 'Changes saved',
        errorOccurred: 'Error occurred',
        checkFields: 'Check fields',
        applyToExpense: 'Преобразовать расход в обмен',
        applyToIncome: 'Преобразовать доход в обмен',
        cancel: 'Cancel',
      },
      ru: {
        editTitleExpense: 'Расход в обмен',
        editTipExpense: 'Преобразование расходной операции в обменную',
        editTitleIncome: 'Доход в обмен',
        editTipIncome: 'Преобразование доходной операции в обменную',
        infoTransformExpense: 'Укажите счет, с которого было поступление и категорию расхода.<br>Если вы уже вносили расходную операцию по этому счету, выберите ее из списка.',
        infoTransformIncome: 'Укажите счет, на который было поступление.<br>Если вы уже вносили доходную операцию по этому счету, выберите ее из списка.',
        sum_from: 'Сумма',
        sum_to: 'Поступит',
        commission: 'Комиссия',
        account_from: 'Со счета',
        account_to: 'На счет',
        categoryExpense: 'Категория расхода',
        changesSaved: 'Изменения сохранены',
        errorOccurred: 'Произошла ошибка',
        checkFields: 'Проверьте поля',
        applyToExpense: 'Преобразовать расход в обмен',
        applyToIncome: 'Преобразовать доход в обмен',
        cancel: 'Отмена',
      }
    }
  }
}
</script>

<style scoped lang="scss">
.form {
  @apply grid row-gap-12;
  &.selecting-expense {
    .account-from-select {
      order: 0;
    }

    .account-to-select {
      order: 2;
    }

    .transaction-select {
      order: 1;
    }

    .actions {
      order: 3;
    }
  }
}
</style>